import { getReadAnnouncements, isExpertAnnouncement } from "./helpers";
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import {
  AnnouncementItem,
  AnnouncementPayload,
  AnnouncementResponse,
  DeleteAnnouncementPayload,
} from "./types";
import { MessageResponse } from "../../../common/types/apiTypes";
import {
  getAllAnnouncements,
  postNewAnnouncement,
  postUpdateAnnouncement,
  postDeleteAnnouncement,
  getActiveAnnouncements,
} from "./announcementsApi";
import { createMsgCommandAsyncThunk } from "../../../app/createApiAsyncThunk";

type Entities = {
  [key: string]: any;
};

export type SliceState = {
  ids: [];
  entities: Entities;
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

const announcementAdapter = createEntityAdapter<AnnouncementItem>({
  sortComparer: (a, b) => a.startAt.localeCompare(b.startAt),
});

export const fetchAnnouncements = createAsyncThunk<
  MessageResponse<AnnouncementResponse>,
  void,
  { state: RootState }
>("settings/fetchAnnouncements", async () => {
  return await getAllAnnouncements();
});

export const createAnnouncement = createMsgCommandAsyncThunk<
  AnnouncementPayload,
  AnnouncementItem
>("settings/createAnnouncement", async (payload: AnnouncementPayload) => {
  const response = await postNewAnnouncement(payload);
  return response.result;
});

export const updateAnnouncement = createMsgCommandAsyncThunk<
  AnnouncementItem,
  AnnouncementItem
>("settings/updateAnnouncement", async (payload: AnnouncementItem) => {
  const response = await postUpdateAnnouncement(payload);
  return response.result;
});

export const deleteAnnouncement = createMsgCommandAsyncThunk<
  DeleteAnnouncementPayload,
  MessageResponse<{ id: string }>
>("settings/deleteAnnouncement", async (payload: DeleteAnnouncementPayload) => {
  const response = await postDeleteAnnouncement(payload);
  return response.result;
});

export const fetchActiveAnnouncements = createAsyncThunk<
  MessageResponse<AnnouncementResponse>,
  void,
  { state: RootState }
>("settings/fetchActiveAnnouncements", async () => {
  return await getActiveAnnouncements();
});

export const announcementSlice = createSlice({
  name: "announcements",
  initialState: announcementAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {
    addAnnouncement: announcementAdapter.addOne,
    setAnnouncement: announcementAdapter.updateOne,
    removeAnnouncement: announcementAdapter.removeOne,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchActiveAnnouncements.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchActiveAnnouncements.fulfilled, (state, action) => {
      const items = action.payload.result.items.map((item) => ({
        ...item,
        active: true,
      }));
      announcementAdapter.setAll(state, items);
      state.status = "succeeded";
    });
    builder.addCase(fetchActiveAnnouncements.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchAnnouncements.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchAnnouncements.fulfilled, (state, action) => {
      const existingIds = new Set(Object.keys(state.entities));
      const newItems = action.payload.result.items.filter(
        (item) => !existingIds.has(item.id),
      );
      announcementAdapter.addMany(state, newItems);
      state.status = "succeeded";
    });
    builder.addCase(fetchAnnouncements.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateAnnouncement.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateAnnouncement.fulfilled, (state, action) => {
      if (!isExpertAnnouncement(action.payload.targetGroups)) {
        announcementAdapter.updateOne(state, {
          id: action.payload.id,
          changes: {
            ...action.payload,
          },
        });
      }
      state.status = "succeeded";
    });
    builder.addCase(updateAnnouncement.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(deleteAnnouncement.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(deleteAnnouncement.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(deleteAnnouncement.fulfilled, (state, action) => {
      announcementAdapter.removeOne(state, action.meta.arg.id);
      state.status = "succeeded";
    });
    builder.addCase(createAnnouncement.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(createAnnouncement.fulfilled, (state, action) => {
      if (!isExpertAnnouncement(action.payload.targetGroups)) {
        announcementAdapter.addOne(state, {
          ...action.payload,
        });
      }
      state.status = "succeeded";
    });
    builder.addCase(createAnnouncement.rejected, (state) => {
      state.status = "failed";
    });
  },
});

const selectActiveAnnouncements = (state: RootState) => {
  const readAnnouncementIds = getReadAnnouncements();
  return selectAll(state).filter(
    (announcement: AnnouncementItem | undefined) =>
      !!announcement?.active && !readAnnouncementIds.includes(announcement.id),
  );
};
export const { selectAll, selectById, selectIds } =
  announcementAdapter.getSelectors((state: RootState) => state.announcements);
export { selectActiveAnnouncements };

export const { addAnnouncement, setAnnouncement, removeAnnouncement } =
  announcementSlice.actions;

export default announcementSlice.reducer;
